import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AlertTriangle, FileCheck, MessageSquarePlus, Save, X } from 'lucide-react';
import type { FinancialAidStaffDetailContext } from '../ApplicantDetail';
import { useUpdateFinancialAidApplication } from '../../../../financial-aid/hooks/useFinancialAidApi';
import { asArray, asRecord } from '../../utils/domainData';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import {
  readParentCollectedFinancialAidSnapshot,
  readRawAmount,
  readSnapshotRecord,
} from './staffFinancialAidRawSnapshot';

const readText = (value: unknown, defaultValue = ''): string => {
  const next = String(value ?? '').trim();
  return next || defaultValue;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickNonZeroAmount = (...values: unknown[]): number => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric != null && numeric !== 0) {
      return numeric;
    }
  }
  return 0;
};

const pickFirstNumericAmount = (...values: unknown[]): number => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric != null) {
      return numeric;
    }
  }
  return 0;
};

const sumYstStudentTuition = (value: unknown): number =>
  asArray<Record<string, unknown>>(value).reduce(
    (total, student) => total + (toNumber(student.tuitionAmount) ?? 0),
    0,
  );

const formatCurrency = (value: number): string =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export type ContractAwardDisposition = 'pending' | 'awarded' | 'denied' | 'declined';

export type ContractAwardSummary = {
  disposition: ContractAwardDisposition;
  label: string;
  amount: number;
  percent: number;
  familyPayment: number;
};

type ContractAwardSummaryInput = {
  status?: unknown;
  contractedTuition?: unknown;
  decision?: unknown;
  finalAward?: unknown;
  recommendation?: unknown;
};

const AWARDED_STATUS_TOKENS = new Set(['approved', 'award_accepted']);
const DENIED_STATUS_TOKENS = new Set(['denied']);
const DECLINED_STATUS_TOKENS = new Set(['award_declined']);
const AWARD_DECISION_TOKENS = new Set(['approve', 'approved', 'override']);
const DENIAL_DECISION_TOKENS = new Set(['deny', 'denied']);

const normalizeDecisionToken = (value: unknown): string => readText(value).toLowerCase();

export const buildContractAwardSummary = ({
  status,
  contractedTuition,
  decision,
  finalAward,
  recommendation,
}: ContractAwardSummaryInput): ContractAwardSummary => {
  const safeContractedTuition = Math.max(toNumber(contractedTuition) ?? 0, 0);
  const statusToken = normalizeDecisionToken(status);
  const decisionRecord = asRecord(decision);
  const finalAwardRecord = asRecord(finalAward);
  const recommendationRecord = asRecord(recommendation);
  const decisionType = normalizeDecisionToken(decisionRecord.decisionType);

  if (DENIAL_DECISION_TOKENS.has(decisionType) || DENIED_STATUS_TOKENS.has(statusToken)) {
    return {
      disposition: 'denied',
      label: 'DENIED',
      amount: 0,
      percent: 0,
      familyPayment: safeContractedTuition,
    };
  }

  if (DECLINED_STATUS_TOKENS.has(statusToken)) {
    return {
      disposition: 'declined',
      label: 'DECLINED',
      amount: 0,
      percent: 0,
      familyPayment: safeContractedTuition,
    };
  }

  if (AWARD_DECISION_TOKENS.has(decisionType) || AWARDED_STATUS_TOKENS.has(statusToken)) {
    const amount = pickFirstNumericAmount(
      decisionRecord.awardedAmount,
      finalAwardRecord.scholarshipAmount,
      recommendationRecord.awardedAmount,
      recommendationRecord.recommendedAmount,
    );
    const percent = pickFirstNumericAmount(
      decisionRecord.awardedPercent,
      finalAwardRecord.scholarshipPercent,
      recommendationRecord.awardedPercent,
      recommendationRecord.recommendedPercent,
    );

    return {
      disposition: 'awarded',
      label: 'AWARDED',
      amount,
      percent,
      familyPayment: Math.max(safeContractedTuition - amount, 0),
    };
  }

  return {
    disposition: 'pending',
    label: 'PENDING DECISION',
    amount: 0,
    percent: 0,
    familyPayment: safeContractedTuition,
  };
};

const toEpochSeconds = (value: unknown): number => {
  const numeric = toNumber(value);
  if (numeric == null) {
    return Math.floor(Date.now() / 1000);
  }
  return numeric > 1_000_000_000_000 ? Math.floor(numeric / 1000) : Math.floor(numeric);
};

const humanizeToken = (value: unknown, fallback: string): string => {
  const token = readText(value);
  if (!token) return fallback;
  const spaced = token.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
};

type SaveFeedback = {
  tone: 'success' | 'error';
  message: string;
};

const severityToneClass = (severity: string): string => {
  const normalized = severity.toLowerCase();
  if (normalized === 'high') {
    return 'bg-vibrant-coral/10 text-vibrant-coral';
  }
  if (normalized === 'medium') {
    return 'bg-amber-50 text-amber-500';
  }
  return 'bg-gray-100 text-irongrey/60';
};

export function ContractTab() {
  const ctx = useOutletContext<FinancialAidStaffDetailContext>();
  const review = ctx.review;
  const updateApplication = useUpdateFinancialAidApplication();
  const hasReview = Boolean(review);

  const reviewRecord = asRecord(review as unknown);
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);
  const metrics = asRecord(application.metrics);
  const reviewSummary = asRecord(sections.reviewSummary);
  const recommendation = asRecord(reviewRecord.recommendation);
  const finalAward = asRecord(application.finalAward);
  const decision = asRecord(application.decision);
  const additionalInfo = asRecord(sections.additionalInfo);
  const specialCircumstances = asRecord(sections.specialCircumstances);
  const rawSnapshot = readParentCollectedFinancialAidSnapshot(review);
  const rawContract = readSnapshotRecord(rawSnapshot, 'contract');
  const rawRequest = readSnapshotRecord(rawSnapshot, 'request');
  const applicationStatus = readText(application.status).toLowerCase();

  const contractedTuition = pickNonZeroAmount(
    readRawAmount(rawContract.eligibleTuition),
    readRawAmount(rawContract.totalTuition),
    metrics.ystEligibleTuition,
    metrics.ystTotalTuition,
    metrics.totalTuitionBurden,
    recommendation.contractedTuition,
    reviewSummary.ystTuitionTotal,
    reviewSummary.totalTuitionBurden,
    sumYstStudentTuition(sections.ystStudents),
  );
  const requestedAidAmount = pickNonZeroAmount(
    readRawAmount(rawRequest.requestedAmount),
    recommendation.requestedAidAmount,
    finalAward.requestedAidAmount,
    recommendation.recommendedAmount,
    recommendation.awardedAmount,
  );
  const requestedAidPercent = contractedTuition > 0 ? (requestedAidAmount / contractedTuition) * 100 : 0;
  const awardSummary = buildContractAwardSummary({
    status: applicationStatus,
    contractedTuition,
    decision,
    finalAward,
    recommendation,
  });
  const awardAmountClass = awardSummary.disposition === 'awarded' ? 'text-aquamarine' : 'text-white';
  const awardPercentText = awardSummary.disposition === 'pending'
    ? 'not decided'
    : `${Math.round(awardSummary.percent)}%`;

  const savedHardship = readText(
    additionalInfo.additionalComments,
    readText(
      additionalInfo.expectedFinancialChanges,
      readText(
        specialCircumstances.additionalDetails,
        readText(
          specialCircumstances.financialHardshipExplanation,
          readText(specialCircumstances.notes)
        )
      )
    )
  );

  const [localHardship, setLocalHardship] = useState(savedHardship);
  const [flagNotes, setFlagNotes] = useState<Record<string, string>>({});
  const [hardshipFeedback, setHardshipFeedback] = useState<SaveFeedback | null>(null);
  const [flagFeedback, setFlagFeedback] = useState<SaveFeedback | null>(null);
  const [savingHardship, setSavingHardship] = useState(false);
  const [savingFlagId, setSavingFlagId] = useState<string | null>(null);

  useEffect(() => {
    setLocalHardship(savedHardship);
  }, [savedHardship]);

  const isHardshipDirty = localHardship.trim() !== savedHardship.trim();
  const canEditHardship = applicationStatus === 'draft';

  const rawFlags = useMemo(() => {
    const directFlags = asArray<Record<string, unknown>>(reviewRecord.flags);
    if (directFlags.length > 0) {
      return directFlags;
    }
    return asArray<Record<string, unknown>>(recommendation.flags);
  }, [recommendation.flags, reviewRecord.flags]);

  const activeFlags = useMemo(
    () => rawFlags.filter((flag) => !Boolean(flag.isResolved)),
    [rawFlags]
  );

  const flagsForDisplay = activeFlags.length > 0 ? activeFlags : rawFlags;

  const existingStaffInternalNotes = useMemo(() => {
    return asArray<Record<string, unknown>>(additionalInfo.staffInternalNotes)
      .map((entry, index) => {
        const note = asRecord(entry);
        const content = readText(note.content);
        if (!content) return null;
        return {
          id: readText(note.id, `note-${index}`),
          author: readText(note.author, 'Staff Reviewer'),
          createdAt: toEpochSeconds(note.createdAt),
          content,
        };
      })
      .filter((entry): entry is { id: string; author: string; createdAt: number; content: string } => entry != null);
  }, [additionalInfo.staffInternalNotes]);

  const staffAuthor = readText(application.assigneeName, 'Staff Reviewer');

  const handleHardshipSave = async () => {
    if (!canEditHardship || !isHardshipDirty || savingHardship) {
      return;
    }
    setSavingHardship(true);
    setHardshipFeedback(null);
    try {
      await updateApplication.mutateAsync({
        id: ctx.applicationId,
        sections: {
          additionalInfo: {
            additionalComments: localHardship.trim() || null,
          },
        },
      });
      setHardshipFeedback({ tone: 'success', message: 'Hardship explanation saved.' });
      ctx.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save hardship explanation right now.';
      setHardshipFeedback({ tone: 'error', message });
    } finally {
      setSavingHardship(false);
    }
  };

  const handleSaveFlagNote = async (flagId: string, flagLabel: string) => {
    const draft = readText(flagNotes[flagId]);
    if (!draft || savingFlagId) {
      return;
    }

    setSavingFlagId(flagId);
    setFlagFeedback(null);
    try {
      const now = Math.floor(Date.now() / 1000);
      const nextNotes = [
        ...existingStaffInternalNotes,
        {
          id: `flag-note-${flagId}-${now}`,
          author: staffAuthor,
          createdAt: now,
          content: `[Flag: ${flagLabel}] ${draft}`,
        },
      ];

      await updateApplication.mutateAsync({
        id: ctx.applicationId,
        sections: {
          additionalInfo: {
            staffInternalNotes: nextNotes,
          },
        },
      });

      setFlagNotes((prev) => ({ ...prev, [flagId]: '' }));
      setFlagFeedback({
        tone: 'success',
        message: 'Investigation note saved to the notes section.',
      });
      ctx.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save this investigation note right now.';
      setFlagFeedback({ tone: 'error', message });
    } finally {
      setSavingFlagId(null);
    }
  };

  if (!hasReview) {
    return <p className="text-sm text-[#6B7280]">Application data is syncing.</p>;
  }

  return (
    <div data-testid="financial-aid-contract-flags-tab" className="space-y-8 max-w-5xl animate-in fade-in duration-300">
      <section
        data-testid="financial-aid-contract-summary-banner"
        className="bg-[#1C3B3E] text-white rounded-xl p-6 shadow-lg shadow-black/5"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-white/10 rounded-lg">
            <FileCheck size={20} className="text-aquamarine" />
          </div>
          <h2 className="text-lg font-bold tracking-tight text-white">Tuition vs Scholarship Request</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase opacity-50 mb-2">CONTRACTED TUITION</p>
            <p className="text-2xl font-bold text-white">{formatCurrency(contractedTuition)}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase opacity-50 mb-2">REQUESTED AID</p>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold text-aquamarine">{formatCurrency(requestedAidAmount)}</p>
              <span className="text-xs font-bold opacity-60">{Math.round(requestedAidPercent)}%</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase opacity-50 mb-2">{awardSummary.label}</p>
            <div className="flex items-baseline gap-2">
              <p className={`text-2xl font-bold ${awardAmountClass}`}>{formatCurrency(awardSummary.amount)}</p>
              <span className="text-xs font-bold opacity-60">{awardPercentText}</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase opacity-50 mb-2">FAMILY PAYMENT</p>
            <p className="text-2xl font-bold text-white">{formatCurrency(awardSummary.familyPayment)}</p>
          </div>
        </div>
      </section>

      <section
        data-testid="financial-aid-contract-hardship"
        className="bg-white rounded-xl border p-6 shadow-sm"
        style={{ borderColor: '#E5E7EB' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">Hardship Explanation</h3>
          <button
            type="button"
            onClick={() => {
              void handleHardshipSave();
            }}
            disabled={!canEditHardship || !isHardshipDirty || savingHardship}
            className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
              canEditHardship && isHardshipDirty
                ? 'text-primary hover:text-primary-hover cursor-pointer'
                : 'text-gray-300 cursor-not-allowed'
            }`}
          >
            <Save size={14} /> Save Changes
          </button>
        </div>

        <textarea
          value={localHardship}
          onChange={(event) => setLocalHardship(event.target.value)}
          placeholder="Provide details about the family's financial hardship..."
          className="w-full bg-background-light border border-gray-100 rounded-xl p-4 text-sm text-irongrey focus:ring-primary focus:border-primary resize-none min-h-[120px] placeholder:text-irongrey/30"
          disabled={!canEditHardship}
        />

        {!canEditHardship ? (
          <p className="mt-3 text-xs font-semibold text-irongrey/40">
            Hardship text is read-only after submission; use notes below for additional staff context.
          </p>
        ) : null}

        {hardshipFeedback?.tone === 'error' ? (
          <SupportErrorMessage className="mt-3 text-xs">
            {hardshipFeedback.message}
          </SupportErrorMessage>
        ) : hardshipFeedback ? (
          <p className="mt-3 text-xs font-semibold text-primary">{hardshipFeedback.message}</p>
        ) : null}
      </section>

      <section data-testid="financial-aid-contract-review-flags" className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#111827]">Review Flags</h2>
          <span className="text-[10px] font-bold text-vibrant-coral bg-vibrant-coral/10 px-2 py-1 rounded-full uppercase tracking-wider">
            {activeFlags.length} Action Items
          </span>
        </div>

        {flagsForDisplay.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-gray-100 p-8 text-center">
            <p className="text-sm text-irongrey/40 font-medium">All flags have been dismissed or addressed.</p>
          </div>
        ) : (
          flagsForDisplay.map((flag, index) => {
            const flagId = readText(flag.id, `flag-${index}`);
            const flagType = humanizeToken(flag.type, 'Review Flag');
            const severity = readText(flag.severity, 'low').toUpperCase();
            const description = readText(
              flag.description,
              readText(flag.triggerCondition, 'Flagged for additional review by staff.')
            );

            return (
              <div
                key={flagId}
                data-testid="financial-aid-contract-flag-item"
                className="bg-white rounded-xl p-6 border shadow-sm group relative animate-in slide-in-from-top-2 duration-300"
                style={{ borderColor: '#E5E7EB' }}
              >
                <button
                  type="button"
                  disabled
                  aria-label="Dismiss flag"
                  className="absolute top-4 right-4 p-1.5 text-irongrey/20 rounded-lg cursor-not-allowed"
                  title="Flag dismissal is managed by review workflow automation."
                >
                  <X size={18} />
                </button>

                <div className="flex justify-between items-start mb-4 pr-10">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${severityToneClass(severity)}`}>
                      <AlertTriangle size={18} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#111827]">{flagType}</h3>
                      <p className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">
                        {severity} Severity
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-irongrey mb-6 leading-relaxed">{description}</p>

                <div className="pt-5 border-t border-gray-50">
                  <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
                    Staff Investigation Notes
                  </label>
                  <div className="flex flex-col gap-3">
                    <textarea
                      className="w-full bg-background-light border border-gray-100 rounded-xl p-4 text-sm focus:ring-primary focus:border-primary resize-none placeholder:text-irongrey/30"
                      placeholder="Add notes from your review of this flag..."
                      rows={2}
                      value={flagNotes[flagId] || ''}
                      onChange={(event) => setFlagNotes((prev) => ({ ...prev, [flagId]: event.target.value }))}
                    />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          void handleSaveFlagNote(flagId, flagType);
                        }}
                        disabled={!readText(flagNotes[flagId]) || savingFlagId === flagId}
                        className={`flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-lg transition-all ${
                          !readText(flagNotes[flagId]) || savingFlagId === flagId
                            ? 'text-irongrey/20 bg-gray-50 cursor-not-allowed'
                            : 'text-primary bg-primary/5 hover:bg-primary/10'
                        }`}
                      >
                        <MessageSquarePlus size={14} />
                        Save to Notes Section
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {flagFeedback?.tone === 'error' ? (
          <SupportErrorMessage className="text-xs">
            {flagFeedback.message}
          </SupportErrorMessage>
        ) : flagFeedback ? (
          <p className="text-xs font-semibold text-primary">{flagFeedback.message}</p>
        ) : null}
      </section>
    </div>
  );
}
